import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';

interface EventCategory {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  image: string;
  accentColor: string;
}

const eventCategories: EventCategory[] = [
  {
    id: 'wedding',
    title: 'Wedding',
    subtitle: 'Luxury invitation templates',
    description: 'Elegant wedding invitations with RSVP, gallery, and maps integration.',
    features: ['RSVP', 'Gallery', 'Maps'],
    image: '/hero/wedding_hero.jpg',
    accentColor: '#D4AF37',
  },
  {
    id: 'birthday',
    title: 'Birthday',
    subtitle: 'Fun and colorful invitations',
    description: 'Vibrant birthday invitations with countdown timers and RSVP.',
    features: ['Countdown', 'RSVP'],
    image: '/hero/birthday_hero.jpg',
    accentColor: '#FF6B6B',
  },
  {
    id: 'housewarming',
    title: 'Housewarming',
    subtitle: 'Traditional & modern designs',
    description: 'Welcome guests to your new home with beautiful invitations.',
    features: ['Gallery', 'Directions'],
    image: '/hero/housewarming_hero.jpg',
    accentColor: '#4CAF50',
  },
  {
    id: 'pooja',
    title: 'Satyanarayan Pooja',
    subtitle: 'Sacred invitation collection',
    description: 'Traditional and blessed invitation templates for religious ceremonies.',
    features: ['Sacred', 'Traditional'],
    image: '/hero/pooja_hero.jpg',
    accentColor: '#FF9800',
  },
  {
    id: 'graduation',
    title: 'Graduation',
    subtitle: 'Celebrate achievements',
    description: 'Mark academic milestones with elegant graduation announcements.',
    features: ['Achievement', 'Gallery'],
    image: '/hero/graduation_hero.jpg',
    accentColor: '#2196F3',
  },
  {
    id: 'baby-shower',
    title: 'Baby Shower',
    subtitle: 'Joyous announcements',
    description: 'Beautiful invitations to celebrate the upcoming arrival of your little one.',
    features: ['Registry', 'RSVP'],
    image: '/hero/baby_shower_hero.jpg',
    accentColor: '#00BCD4',
  },
  {
    id: 'corporate',
    title: 'Corporate Event',
    subtitle: 'Professional invitations',
    description: 'Polished invitations for conferences, launches, and corporate gatherings.',
    features: ['Professional', 'Brand Kit'],
    image: '/hero/corporate_hero.jpg',
    accentColor: '#6366F1',
  },
  {
    id: 'reception',
    title: 'Reception',
    subtitle: 'Grand celebration invites',
    description: 'Magnificent invitations for wedding receptions and celebrations.',
    features: ['Elegant', 'RSVP'],
    image: '/hero/reception_hero.jpg',
    accentColor: '#9C27B0',
  },
];

interface InteractiveImageAccordionProps {
  autoPlayInterval?: number;
  className?: string;
}

export function InteractiveImageAccordion({
  autoPlayInterval = 4000,
  className = '',
}: InteractiveImageAccordionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState<Record<number, boolean>>({});
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReduceMotion = useReducedMotion();

  // Preload next image
  useEffect(() => {
    const nextIndex = (activeIndex + 1) % eventCategories.length;
    const img = new Image();
    img.src = eventCategories[nextIndex].image;
  }, [activeIndex]);

  // Auto-play logic
  const startAutoPlay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % eventCategories.length);
    }, autoPlayInterval);
  }, [autoPlayInterval]);

  useEffect(() => {
    if (!isHovered) {
      startAutoPlay();
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHovered, startAutoPlay]);

  const handleInteract = (index: number) => {
    setIsHovered(true);
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleImageLoad = (index: number) => {
    setIsLoaded((prev) => ({ ...prev, [index]: true }));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const dx = touchStartXRef.current - touchEndX;
    const dy = touchStartYRef.current - touchEndY;

    // Ignore swipes where vertical delta dominates horizontal delta (|dy| > |dx|)
    if (Math.abs(dy) <= Math.abs(dx) && Math.abs(dx) > 40) {
      if (dx > 0) {
        handleInteract((activeIndex + 1) % eventCategories.length);
      } else {
        handleInteract((activeIndex - 1 + eventCategories.length) % eventCategories.length);
      }
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  const handleTouchCancel = () => {
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-[500px] lg:h-[600px] rounded-[24px] lg:rounded-[32px] overflow-hidden bg-gradient-to-br from-surface to-surface-lowest border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.25)] ${className}`}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-status-processing/5 pointer-events-none" />

      {/* Accordion Container */}
      <div className="flex gap-1 lg:gap-2 h-full w-full p-2 lg:p-3" role="tablist">
        {eventCategories.map((event, index) => {
          const isActive = index === activeIndex;
          const isLoadedImage = isLoaded[index];

          return (
            <div
              key={event.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${event.id}`}
              id={`tab-${event.id}`}
              tabIndex={0}
              onMouseEnter={() => handleInteract(index)}
              onFocus={() => handleInteract(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleInteract(index);
                } else if (e.key === 'ArrowRight') {
                  handleInteract((index + 1) % eventCategories.length);
                } else if (e.key === 'ArrowLeft') {
                  handleInteract((index - 1 + eventCategories.length) % eventCategories.length);
                }
              }}
              className={`
                relative h-full cursor-pointer transition-all ease-out rounded-[16px] lg:rounded-[24px] overflow-hidden
                ${shouldReduceMotion ? 'duration-0' : 'duration-500'}
                ${isActive ? 'w-full sm:w-[55%] md:w-[60%] lg:w-[65%] flex-none z-10 shadow-2xl' : 'hidden sm:block sm:flex-1'}
              `}
            >
              {/* Image */}
              <div 
                className="relative h-full w-full"
                id={`panel-${event.id}`}
                role="tabpanel"
                aria-labelledby={`tab-${event.id}`}
              >
                {!isLoadedImage && (
                  <div className={`absolute inset-0 bg-surface ${shouldReduceMotion ? '' : 'animate-pulse'}`} />
                )}
                <img
                  src={event.image}
                  alt={`${event.title} Invitation Template`}
                  className={`
                    w-full h-full object-cover transition-all
                    ${shouldReduceMotion ? 'duration-0' : 'duration-700'}
                    ${isActive ? 'scale-100' : 'scale-105 grayscale-[30%]'}
                  `}
                  onLoad={() => handleImageLoad(index)}
                  loading={index === 0 ? "eager" : "lazy"}
                />

                {/* Gradient overlay */}
                <div
                  className={`
                    absolute inset-0 transition-opacity pointer-events-none
                    ${shouldReduceMotion ? 'duration-0' : 'duration-500'}
                    ${isActive
                      ? 'bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100'
                      : 'bg-black/40 opacity-60'
                    }
                  `}
                />

                {/* Active card content */}
                <AnimatePresence mode="wait">
                  {isActive && (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -20 }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.5, ease: 'easeOut' }}
                      className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8 overflow-hidden"
                    >
                      {/* Event Title */}
                      <motion.h3
                        initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: shouldReduceMotion ? 0 : 0.1, duration: shouldReduceMotion ? 0 : 0.4 }}
                        className="text-2xl lg:text-3xl font-bold text-white mb-2 whitespace-nowrap"
                        style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
                      >
                        {event.title}
                      </motion.h3>

                      {/* Subtitle */}
                      <motion.p
                        initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: shouldReduceMotion ? 0 : 0.15, duration: shouldReduceMotion ? 0 : 0.4 }}
                        className="text-sm lg:text-base text-white/90 mb-3 whitespace-nowrap"
                      >
                        {event.subtitle}
                      </motion.p>

                      {/* Description */}
                      <motion.p
                        initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: shouldReduceMotion ? 0 : 0.2, duration: shouldReduceMotion ? 0 : 0.4 }}
                        className="text-xs lg:text-sm text-white/80 mb-4 max-w-[280px]"
                      >
                        {event.description}
                      </motion.p>

                      {/* Feature tags */}
                      <motion.div
                        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: shouldReduceMotion ? 0 : 0.25, duration: shouldReduceMotion ? 0 : 0.4 }}
                        className="flex flex-wrap gap-2"
                      >
                        {event.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-[10px] lg:text-xs font-medium rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/20 whitespace-nowrap"
                          >
                            {feature}
                          </span>
                        ))}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Inactive card title (rotated) */}
                {!isActive && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span
                      className="text-white text-xs lg:text-sm font-bold tracking-widest uppercase whitespace-nowrap"
                      style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)',
                      }}
                    >
                      {event.title}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile previous/next navigation buttons (< sm) */}
      <div className="absolute inset-y-0 left-0 right-0 flex sm:hidden items-center justify-between px-3 pointer-events-none z-20">
        <button
          type="button"
          onClick={() => handleInteract((activeIndex - 1 + eventCategories.length) % eventCategories.length)}
          className="pointer-events-auto flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-black/50 text-white backdrop-blur-md border border-white/20 hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white transition-transform active:scale-95"
          aria-label="Previous category"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handleInteract((activeIndex + 1) % eventCategories.length)}
          className="pointer-events-auto flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-black/50 text-white backdrop-blur-md border border-white/20 hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white transition-transform active:scale-95"
          aria-label="Next category"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Bottom navigation dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {eventCategories.map((event, index) => (
          <button
            key={event.id}
            onClick={() => {
              setActiveIndex(index);
              setIsHovered(true);
              setTimeout(() => setIsHovered(false), 300);
            }}
            className={`
              w-2 h-2 rounded-full transition-all 
              ${shouldReduceMotion ? 'duration-0' : 'duration-300'}
              ${index === activeIndex
                ? 'bg-white w-6 shadow-lg'
                : 'bg-white/40 hover:bg-white/60 focus:bg-white/80 focus:outline-none focus:ring-2 focus:ring-white/50'
              }
            `}
            aria-label={`View ${event.title}`}
          />
        ))}
      </div>

      {/* Auto-play indicator */}
      <div className="hidden sm:flex absolute top-6 right-6 items-center gap-2 z-10 pointer-events-none">
        <motion.div
          animate={{ opacity: isHovered ? 0.3 : 1 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10"
        >
          <motion.div
            animate={{ scale: isHovered || shouldReduceMotion ? 1 : [1, 1.2, 1] }}
            transition={{ repeat: isHovered || shouldReduceMotion ? 0 : Infinity, duration: 2 }}
            className="w-2 h-2 rounded-full bg-white"
          />
          <span className="text-xs text-white/90 font-medium tracking-wide">
            {isHovered ? 'PAUSED' : 'AUTO'}
          </span>
        </motion.div>
      </div>
    </div>
  );
}

export default InteractiveImageAccordion;